import { useQuery } from '@tanstack/react-query';
import { createTrainingModuleService } from '../services/training-module.service';
import { createTrainingTopicService } from '../services/training-topic.service';
import { createTrainingContentService } from '../services/training-content.service';
import {
  TrainingModuleDto,
  TrainingTopicDto,
  TrainingContentDto,
} from '../types/dtos/training';

interface TrainingStructure {
  modules: TrainingModuleDto[];
  topics: TrainingTopicDto[];
  contents: TrainingContentDto[];
}

/**
 * Hook to fetch the complete training structure (modules, topics, contents)
 * This solves the React hooks rules issue when fetching nested data
 */
export const useTrainingStructure = (organizationId: string, trainingId: string) => {
  return useQuery({
    queryKey: ['training-structure', organizationId, trainingId],
    queryFn: async (): Promise<TrainingStructure> => {
      if (!organizationId || !trainingId) {
        return { modules: [], topics: [], contents: [] };
      }

      try {
        // Step 1: Fetch all modules
        const moduleService = createTrainingModuleService(organizationId, trainingId);
        const modulesResponse = await moduleService.findAll({ isActive: true });
        const modules = modulesResponse.data || [];

        // Step 2: Fetch all topics for all modules
        const topicsPromises = modules.map((module) => {
          const topicService = createTrainingTopicService(organizationId, trainingId, module.id);
          return topicService.findAll({ isActive: true });
        });
        const topicsResponses = await Promise.all(topicsPromises);
        const topics = topicsResponses.flatMap((response) => response.data || []);

        // Step 3: Fetch all contents for all topics
        const contentsPromises = topics.map((topic) => {
          const module = modules.find((m) => m.id === topic.moduleId);
          if (!module) return Promise.resolve({ data: [] });

          const contentService = createTrainingContentService(
            organizationId,
            trainingId,
            module.id,
            topic.id
          );
          return contentService.findAll({ isActive: true });
        });
        const contentsResponses = await Promise.all(contentsPromises);
        const contents = contentsResponses.flatMap((response) => response.data || []);

        return { modules, topics, contents };
      } catch (error) {
        console.error('Error fetching training structure:', error);
        return { modules: [], topics: [], contents: [] };
      }
    },
    enabled: !!organizationId && !!trainingId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

